import axios from 'axios';
import axiosRetry from '../helpers/retry.helper';

import restaurant from './mock-data/restaurant.json';
import {BASKET_MODE, ITEM_ORIGIN} from '../constants/constant';
import mockBasket from './mock-data/basket.json';
import config from '../config';

const axiosRetryConf = {
    retries: 3,
    retryCondition: (response) => {
      return response.status === 202 ||
        (
          response.data[0] === 'A' &&
          response.data[1] === 'c' &&
          response.data[2] === 'c' &&
          response.data[3] === 'e' &&
          response.data[4] === 'p' &&
          response.data[5] === 't' &&
          response.data[6] === 'e' &&
          response.data[7] === 'd'
        );
    }
  };

const transformMockMealForBasket = meal => {
  const mockMeal = restaurant.menu.meals[meal.mealUuid];
  const unitPrice = (Math.round(meal.price * 100) / (meal.quantity * 100)).toFixed(2);
  return {
    uuid: Math.random().toString(),
    restaurantCode: mockMeal.restaurantCode,
    items: [{
      uuid: Math.random().toString(),
      mealUuid: meal.mealUuid,
      mealData: {
        name: mockMeal.name,
        categoryCode: mockMeal.categoryName
      },
      quantity: meal.quantity,
      size: meal.size,
      selectedOptions: meal.selectedOptions,
      unitPrice,
      comments: meal.comments,
      price: meal.price
    }]
  };
};

const createBasket = (restaurantCode, basket, item, itemOrigin, orderSource) => {
  if (config.mockApi) {
    const transformedMeal = transformMockMealForBasket(item);
    return new Promise(resolve => {
      if (basket.items.length === 0) {
        return resolve(transformedMeal);
      }

      return resolve(Object.assign({}, basket, {
        items: [...transformedMeal.items, ...basket.items]
      }));
    });
  }

  const url = `${config.apiUrl}/basket`;
  const data = {
    restaurantCode: restaurantCode,
    orderSource,
    items: [{
      mealUuid: item.mealUuid,
      quantity: item.quantity,
      selectedOptions: item.selectedOptions,
      size: item.size,
      comments: item.comments,
      origin: itemOrigin || ITEM_ORIGIN.MENU
    }]
  };

  return axios
    .post(url, data, {headers: {'X-FOODit-Restaurant-Code': restaurantCode}})
    .then(res => res.data);
};

const updateBasket = (basket, updatedItem, restaurantCode, itemOrigin) => {
   if (config.mockApi) {
     const basketItems = basket.items;
     const index = basketItems.findIndex(item => item.uuid === updatedItem.uuid);
     return new Promise((resolve, reject) => {
       if (index > -1) {
         const item = basketItems[index];
         const newItem = Object.assign({}, updatedItem, {
           mealData: item.mealData,
           unitPrice: (Math.round(updatedItem.price * 100) / (updatedItem.quantity * 100)).toFixed(2)
         });

        const items = [
          ...basketItems.slice(0, index),
          newItem,
          ...basketItems.slice(index + 1)
        ];

        return resolve(Object.assign({}, basket, {items}));
      }

      return reject(new Error('basket item is not existed'));
    }, this);
  }

  const url = `${config.apiUrl}/basket/${basket.uuid}/item${updatedItem.uuid ? `/${updatedItem.uuid}` : ''}`;
  const data = {
    mealUuid: updatedItem.mealUuid,
    quantity: updatedItem.quantity,
    selectedOptions: updatedItem.selectedOptions,
    size: updatedItem.size,
    comments: updatedItem.comments,
    origin: itemOrigin || ITEM_ORIGIN.MENU
  };

  return axios
    .put(url, data, {headers: {'X-FOODit-Restaurant-Code': restaurantCode}})
    .then(res => res.data);
};

class BasketApi {
  static updateBasket(type, restaurantCode, basket, item, itemOrigin, orderSource) {
    switch (type) {
      case BASKET_MODE.CREATE:
        if (basket.uuid && !config.mockApi) {
          return updateBasket(basket, item, restaurantCode, itemOrigin);
        }
        return createBasket(restaurantCode, basket, item, itemOrigin, orderSource);
      case BASKET_MODE.EDIT:
        return updateBasket(basket, item, restaurantCode, itemOrigin);
    }
  }

  static addItemStraightToBasket(basketUuid, restaurantCode, item, itemOrigin, orderSource) {
    if (!basketUuid) {
      const url = `${config.apiUrl}/basket`;
      const data = {
        restaurantCode: restaurantCode,
        origin: itemOrigin,
        orderSource,
        items: [{
          mealUuid: item.uuid,
          quantity: 1
        }]
      };

      return axios
        .post(url, data, {headers: {'X-FOODit-Restaurant-Code': restaurantCode}})
        .then(res => res.data);
    }

    const url = `${config.apiUrl}/basket/${basketUuid}/item`;
    const data = {
      mealUuid: item.uuid,
      quantity: 1,
      origin: itemOrigin,
      orderSource
    };

    return axios
      .put(url, data, {headers: {'X-FOODit-Restaurant-Code': restaurantCode}})
      .then(res => res.data);
  }

  static getBasket(basketUuid, restaurantCode) { // eslint-disable-line no-unused-vars
    if (config.mockApi) {
      return new Promise(resolve => {
        return resolve(Object.assign({}, mockBasket ));
      });
    }

    const url = `${config.apiUrl}/basket/${basketUuid}`;

    return axios
      .get(url, {headers: {'X-FOODit-Restaurant-Code': restaurantCode}})
      .then(res => res.data);
  }

  static reorder(orderId, restaurantCode) {
    const url = `${config.apiUrl}/basket/reorder/${orderId}`;

    return axios
      .get(url, {headers: {'X-FOODit-Restaurant-Code': restaurantCode}})
      .then(res => res.data);
  }

  static updateFulfillment(basketUuid, data) {
    const url = `${config.apiUrl}/basket/${basketUuid}/fulfillment`;

    axiosRetry(axios, axiosRetryConf);

    return axios
      .put(url, data)
      .then(res => res.data);
  }

  static removeBasketItem(basketUuid, itemUuid) {
    const url = `${config.apiUrl}/basket/${basketUuid}/item/${itemUuid}`;

    return axios
      .delete(url)
      .then(res => res.data);
  }

  static updateBasketItem(basketUuid, itemUuid, data) {
    const url = `${config.apiUrl}/basket/${basketUuid}/item/${itemUuid}`;

    return axios
      .put(url, data)
      .then(res => res.data);
  }

  static setBasketConsumer(basketUuid, data) {
    const url = `${config.apiUrl}/basket/${basketUuid}/consumer`;

    return axios
      .put(url, data)
      .then(res => res.data);
  }

  static setBasketFulfillAt(basketUuid, fulfillAt) {
    const url = `${config.apiUrl}/basket/${basketUuid}/fulfillAt`;
    return axios
      .put(url, {fulfillAt})
      .then(res => res.data);
  }

  static setPaymentMethod(basketUuid, data) {
    const url = `${config.apiUrl}/basket/${basketUuid}/payment`;

    return axios
      .put(url, data)
      .then(res => res.data);
  }

  static addVoucher(basketUuid, data) {
    const url = `${config.apiUrl}/basket/${basketUuid}/voucher`;

    return axios
      .post(url, data)
      .then(res => res.data);
  }

  static placeBasketOrder(basketUuid, data) {
    const url = `${config.apiUrl}/basket/${basketUuid}/order`;

    return axios
      .post(url, data)
      .then(res => res.data);
  }
}

export default BasketApi;



// WEBPACK FOOTER //
// ./api/basket.api.js